import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Bookmark, Brain, Layers, X } from "lucide-react";
import type { InSnipCard } from "./types";
import { fetchCard } from "./sessionApi";
import { dueCount } from "./Recall";

/**
 * Your Library: the knowledge companion's home for everything the reader chose to keep. Instead of
 * a flat bookmark filter, it shows the shape of what you've collected (ideas kept, topics covered,
 * how many are due to recall) and gives a one-tap way into a Recall session. This is what makes
 * InSnip feel like something you're building, not just a feed you scroll.
 */
export function LibraryModal(props: {
  cards: InSnipCard[];
  savedIds: string[];
  onOpenCard: (card: InSnipCard) => void;
  onStartRecall: () => void;
  onUnsave: (id: string) => void;
  onClose: () => void;
}) {
  // Resolve saved ideas from the loaded feed first, then best-effort fetch the rest (older saves
  // that aren't in today's deck) so the library stays complete across days. Falls back gracefully
  // to whatever is already loaded when the API is unavailable.
  const loaded = useMemo(() => new Map(props.cards.map((c) => [c.id, c])), [props.cards]);
  const [extra, setExtra] = useState<Map<string, InSnipCard>>(new Map());

  useEffect(() => {
    const missing = props.savedIds.filter((id) => !loaded.has(id) && !extra.has(id));
    if (missing.length === 0) return;
    let alive = true;
    void Promise.all(missing.slice(0, 30).map((id) => fetchCard(id))).then((results) => {
      if (!alive) return;
      setExtra((prev) => {
        const next = new Map(prev);
        for (const c of results) if (c) next.set(c.id, c);
        return next;
      });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.savedIds, loaded]);

  const savedCards = useMemo(
    () =>
      props.savedIds
        .map((id) => loaded.get(id) ?? extra.get(id))
        .filter((c): c is InSnipCard => Boolean(c)),
    [props.savedIds, loaded, extra],
  );

  const topics = useMemo(() => {
    const set = new Set<string>();
    for (const c of savedCards) if (c.category) set.add(String(c.category));
    return set.size;
  }, [savedCards]);

  const due = useMemo(() => dueCount(savedCards), [savedCards]);

  return (
    <section
      className="insight-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Your library"
      onClick={props.onClose}
    >
      <div className="library-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="quiz-close icon-button ghost"
          onClick={props.onClose}
          aria-label="Close library"
        >
          <X size={18} />
        </button>

        <span className="recall-eyebrow"><Bookmark size={13} aria-hidden="true" /> Your library</span>
        <h2 className="library-title">Everything you've kept</h2>

        <div className="library-stats" role="group" aria-label="Library summary">
          <div>
            <strong>{savedCards.length}</strong>
            <small>idea{savedCards.length === 1 ? "" : "s"} kept</small>
          </div>
          <div>
            <strong>{topics}</strong>
            <small>topic{topics === 1 ? "" : "s"}</small>
          </div>
          <div>
            <strong>{due}</strong>
            <small>due to recall</small>
          </div>
        </div>

        {savedCards.length ? (
          <button type="button" className="library-recall-cta" onClick={props.onStartRecall}>
            <Brain size={16} aria-hidden="true" />
            <span>{due ? `Recall ${due} idea${due === 1 ? "" : "s"} now` : "Review your ideas"}</span>
            <ArrowRight size={14} aria-hidden="true" />
          </button>
        ) : null}

        {savedCards.length ? (
          <div className="library-list" role="list">
            {savedCards.map((c) => (
              <div key={c.id} className="library-item" role="listitem">
                <button type="button" className="library-item-main" onClick={() => props.onOpenCard(c)}>
                  {c.category ? <span className="library-item-cat">{c.category}</span> : null}
                  <span className="library-item-headline">{c.headline}</span>
                </button>
                <button
                  type="button"
                  className="library-item-remove"
                  onClick={() => props.onUnsave(c.id)}
                  aria-label="Remove from library"
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="library-empty">
            <div className="recall-result-icon" aria-hidden="true"><Layers size={26} /></div>
            <p>
              Tap the bookmark on any card to keep it here. Your library becomes a quiet, personal
              knowledge base, and InSnip helps you remember what's in it.
            </p>
            <button type="button" className="ghost-action" onClick={props.onClose}>Start reading</button>
          </div>
        )}
      </div>
    </section>
  );
}
